import addOneLog from "features/panelManagement/logging/actions/set.addOneLog";
import extractCombinedTagKeysFromProduct from "features/product/functions/extractCombinedTagKeysFromProduct";
import firebase from "firebase/app";
import "firebase/firestore";
import { AppThunk } from "services/redux/rootReducer";
import { Product } from "../../types/product";
import removeManyProducts from "./set.removeManyProducts";

const updateOneProduct =
    (id: string, updatedProduct: Product): AppThunk =>
    async (dispatch, getState) => {
        const db = firebase.firestore();
        const batch = db.batch();

        // If id happens to be changed
        if (id !== updatedProduct.id) await dispatch(removeManyProducts([id]));

        const updatedRef = db.collection("products").doc(updatedProduct.id);
        const subCollectionRef = updatedRef.collection("tables");

        const originalTableIds =
            getState().products.find((product) => product.id === id)
                ?.tableIds ?? [];
        const updatedTableIds = updatedProduct.tableIds;

        // have to do this to preserve order of current tables instead of using currentTableIds
        const arrangedTableIds: string[] = [];

        // add tables to subcollections
        await Promise.all(
            updatedTableIds.map(async (tableId) => {
                const updatedTable = updatedProduct.tables[tableId];
                // Assigns id if table has no id, otherwise uses existing one
                const assignedId = originalTableIds.includes(tableId ?? "")
                    ? tableId
                    : undefined;
                const docRef = subCollectionRef.doc(assignedId);
                batch.set(docRef, {
                    ...updatedTable,
                    id: docRef.id,
                });
                arrangedTableIds.push(docRef.id);
            })
        );

        // get table ids not in current table ids for removal
        const removedTableIds = originalTableIds.filter(
            (tableId) => !arrangedTableIds.includes(tableId)
        );

        // remove tables that no longer exist
        await Promise.all(
            removedTableIds.map(async (removedTableId) => {
                const docRef = subCollectionRef.doc(removedTableId);
                batch.delete(docRef);
            })
        );

        // create combinedTags
        const combinedTags = extractCombinedTagKeysFromProduct(updatedProduct);

        // remove property tables from product object
        const { tables, ...productWithoutTables } = updatedProduct;

        const dbUpdatedProduct = {
            ...productWithoutTables,
            properties: {
                ...productWithoutTables.properties,
                updateTimestamp: Date.now(),
            },
            tableIds: arrangedTableIds,
            combinedTags,
        } as Product;

        batch.set(updatedRef, dbUpdatedProduct);

        await batch.commit();

        dispatch(
            addOneLog({
                eventInfo: {
                    description: `Updated one product with id ${id}`,
                },
            })
        );

        return;
    };

export default updateOneProduct;
